import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { PageShell } from '@/components/ui/PageShell';
import { EliteCard } from '@/components/ui/EliteCard';
import { supabase } from '@/lib/supabase';
import { db } from '@/lib/db/db';
import { ShieldAlert } from 'lucide-react';
import type { Athlete } from '@/types';

export function PublicProfilePage() {
    const { athleteId } = useParams();
    const [athlete, setAthlete] = useState<Athlete | null>(null);
    const [status, setStatus] = useState<'loading' | 'private' | 'not_found' | 'public'>('loading');

    useEffect(() => {
        const fetchPublicData = async () => {
            if (!athleteId) return;

            // 1. Check visibility in Supabase
            const { data } = await supabase
                .from('athlete_visibility')
                .select('*')
                .eq('athlete_id', athleteId)
                .single();

            if (!data || !data.is_public) {
                setStatus('private');
                return;
            }

            // 2. Fetch basic athlete data (In real app, from Supabase. For MVP, we'll try Dexie if local, or mock if not found)
            const localAthlete = await db.athletes.get(athleteId);
            if (localAthlete) {
                setAthlete(localAthlete);
                setStatus('public');
            } else {
                setStatus('not_found');
            }
        };
        fetchPublicData();
    }, [athleteId]);

    if (status === 'loading') return <PageShell title="Loading..."><div /></PageShell>;

    if (status === 'private') {
        return (
            <PageShell title="Private Profile">
                <div className="flex flex-col items-center justify-center py-20 animate-fade-in-up">
                    <ShieldAlert size={48} className="text-text-muted mb-4" />
                    <h2 className="text-white font-black text-xl mb-2">Profile is Private</h2>
                    <p className="text-text-muted text-center max-w-xs">
                        This athlete's profile is not set to public. Log in if you are a coach with an invite.
                    </p>
                </div>
            </PageShell>
        );
    }

    if (status === 'not_found' || !athlete) {
        return <PageShell title="Not Found"><p className="text-center text-text-muted p-8">Athlete not found.</p></PageShell>;
    }

    return (
        <PageShell title={athlete.name} subtitle="Public Profile">
            <div className="space-y-6 animate-fade-in-up">
                <EliteCard className="p-8 flex flex-col items-center text-center">
                    <div className="w-24 h-24 rounded-full bg-white/5 flex items-center justify-center text-5xl border border-white/10 mb-4 shadow-[0_0_30px_rgba(212,175,55,0.1)]">
                        {athlete.avatar || '🤼'}
                    </div>
                    <h2 className="text-white font-black text-2xl mb-1">{athlete.name}</h2>
                    <p className="text-[11px] text-gold-500 uppercase tracking-widest font-bold mb-4">
                        Palaestra Athlete
                    </p>
                    <div className="flex gap-4 mb-2">
                        <div className="text-center px-4">
                            <p className="text-[10px] text-text-muted uppercase font-bold tracking-widest">Experience</p>
                            <p className="text-white font-bold">{athlete.experienceYears} Years</p>
                        </div>
                        <div className="w-px bg-white/10" />
                        <div className="text-center px-4">
                            <p className="text-[10px] text-text-muted uppercase font-bold tracking-widest">Level</p>
                            <p className="text-white font-bold">{athlete.level}</p>
                        </div>
                    </div>
                </EliteCard>

                <h3 className="text-white font-black uppercase tracking-widest text-sm pt-4">Career Stats</h3>
                <EliteCard className="p-6">
                    <p className="text-text-muted text-sm text-center">
                        Detailed match records are tracked privately. W-L records will appear here when public matches exist.
                    </p>
                </EliteCard>
            </div>
        </PageShell>
    );
}
